import { MouseEvent, useCallback, useContext } from 'react'

import { userActivityEvents } from 'analytics/utils'
import { getCustomerApiClient } from 'api/CustomerApiClient'
import { toast } from 'components/ui/toaster'
import { useAnalytics } from 'context/AnalyticsContext'
import useEcpTranslation from 'hooks/common/useEcpTranslation'
import useDatadog from 'hooks/useDataDog'

import { FileItem } from '../../api/ApiTypes'
import AuthContext from '../../context/AuthContext'
import * as FileUtils from '../../utils/FileUtils'

/**
 * Hook to provide file access functionality to components and handle
 * the current origin and admin session state.
 */
const useFileAccess = (onDownloaded?: (fileId: string) => void) => {
  const { data: authData } = useContext(AuthContext)
  const { t } = useEcpTranslation()
  const datadogRum = useDatadog()
  const { analytics } = useAnalytics()

  const isAdminSession = authData?.isAdminSession

  const checkFileLink = useCallback(
    async (e: MouseEvent, fileId: string) => {
      e.stopPropagation()

      const target = (e.target as HTMLElement).closest('a')
      const href = target.href

      // Should never happen, but just in case
      if (href === '#') {
        const error = new Error('Unavailable')

        toast.error({
          title: t('common.error.something_went_wrong'),
          description: t('common.error.download_failed', {
            error: error.message
          })
        })

        datadogRum?.addError(error, { action: 'download' })

        return
      }

      // Verify link expiration to prevent issues with expired links
      const url = new URL(href)
      const expiration = url.searchParams.get('expires_at')
      const expired = expiration && Number(expiration) < Date.now()

      if (expired) {
        const error = new Error('Expired')

        toast.error({
          title: t('common.error.something_went_wrong'),
          description: t('common.error.download_failed', {
            error: error.message
          })
        })

        datadogRum?.addError(error, { action: 'download' })

        e.preventDefault()

        return
      }

      onDownloaded && onDownloaded(fileId)

      analytics?.addEvents([
        {
          type: userActivityEvents.documentDownloaded,
          details: {
            pagePath: window.location.pathname,
            documentId: fileId
          },
          timestamp: new Date().toISOString()
        }
      ])

      // Track the user has downloaded the file but only if it's not an admin
      // logged in as a customer
      if (!isAdminSession) {
        getCustomerApiClient().trackFileDownloaded({ id: fileId })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datadogRum, isAdminSession, onDownloaded, t]
  )

  const getBinaryContent = useCallback(
    (file: FileItem) => FileUtils.getBinaryContent({ file, isAdminSession }),
    [isAdminSession]
  )

  // Token-mode files can't be downloaded via anchor navigation (the browser
  // can't attach the bearer token). Fetch the blob with the auth header and
  // save it, then mirror checkFileLink's tracking/error handling.
  const downloadFile = useCallback(
    async (file: FileItem) => {
      try {
        await FileUtils.openFile({ file, isAdminSession })

        onDownloaded?.(file._id)
        analytics?.addEvents([
          {
            type: userActivityEvents.documentDownloaded,
            details: {
              pagePath: window.location.pathname,
              documentId: file._id
            },
            timestamp: new Date().toISOString()
          }
        ])
      } catch (error) {
        toast.error({
          title: t('common.error.something_went_wrong'),
          description: t('common.error.download_failed', {
            error: error instanceof Error ? error.message : String(error)
          })
        })

        datadogRum?.addError(error, { action: 'download' })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datadogRum, isAdminSession, onDownloaded, t]
  )

  return {
    getBinaryContent,
    checkFileLink,
    downloadFile
  }
}

export default useFileAccess
